import React from 'react';
import { Users, ShoppingCart, Package, Settings, Calendar, MessageSquare, Wrench, TrendingUp, FileText, MapPin, Store, Shield, Database, Smartphone } from "lucide-react";
import { CheckCircle } from "lucide-react";
import ModuleCard, { ModuleStatus } from './ModuleCard';
import SummaryCard from './SummaryCard';
import TechStackCard from './TechStackCard';
import RecommendationsCard from './RecommendationsCard';
import ConclusionCard from './ConclusionCard';

const SystemStatusReport: React.FC = () => {
  const modules: ModuleStatus[] = [
    {
      name: 'Управление клиентами',
      status: 'working',
      progress: 95,
      functionality: [
        '✅ Добавление физических и юридических лиц',
        '✅ Редактирование информации клиентов',
        '✅ Поиск и фильтрация клиентов',
        '✅ История заказов и аренд клиента',
        '✅ Система лояльности (белые/серые/черные списки)',
        '✅ Интеграция с мессенджерами (WhatsApp/Telegram)',
        '✅ Раскрытие подробностей клиента в таблице',
        '✅ Паспортные данные и банковские реквизиты'
      ],
      icon: <Users className="h-5 w-5" />
    },
    {
      name: 'Система заказов',
      status: 'working',
      progress: 90,
      functionality: [
        '✅ Создание заказов с выбором клиента',
        '✅ Управление статусами заказов',
        '✅ Расчет стоимости и применение скидок',
        '✅ Выбор периода аренды и тарифов',
        '✅ Интеграция с инвентарем',
        '✅ Статистика заказов',
        '✅ Фильтрация и сортировка',
        '⚠️ Улучшение TypeScript типизации'
      ],
      icon: <ShoppingCart className="h-5 w-5" />
    },
    {
      name: 'Управление инвентарем',
      status: 'working',
      progress: 88,
      functionality: [
        '✅ Добавление товаров в инвентарь',
        '✅ Отслеживание статусов (доступен/арендован/обслуживание)',
        '✅ Ценообразование (почасовая/посуточная аренда)',
        '✅ Категоризация товаров',
        '✅ Поиск доступного инвентаря',
        '✅ История использования',
        '✅ API для выбора в заказах',
        '⚠️ Исправление TypeScript ошибок в categoryService'
      ],
      icon: <Package className="h-5 w-5" />
    },
    {
      name: 'Система аренды',
      status: 'working',
      progress: 85,
      functionality: [
        '✅ Создание договоров аренды',
        '✅ Управление периодами аренды',
        '✅ Комплекты для аренды',
        '✅ Расчет стоимости по тарифам',
        '✅ Статусы аренды',
        '✅ Интеграция с инвентарем',
        '✅ Календарь аренды'
      ],
      icon: <Calendar className="h-5 w-5" />
    },
    {
      name: 'Торговые точки и локации',
      status: 'working',
      progress: 92,
      functionality: [
        '✅ Управление точками аренды',
        '✅ Настройка адресов и контактов',
        '✅ Привязка инвентаря к точкам',
        '✅ Кросс-локационные заказы',
        '✅ Перемещение инвентаря между точками'
      ],
      icon: <MapPin className="h-5 w-5" />
    },
    {
      name: 'Магазин и товары',
      status: 'working',
      progress: 87,
      functionality: [
        '✅ Управление товарами магазина',
        '✅ Учет остатков',
        '✅ Ценообразование',
        '✅ Привязка к заказам',
        '✅ Управление сроками годности',
        '✅ Интеграция с основным инвентарем'
      ],
      icon: <Store className="h-5 w-5" />
    },
    {
      name: 'Система промокодов',
      status: 'working',
      progress: 90,
      functionality: [
        '✅ Создание промокодов',
        '✅ Настройка скидок (процент/сумма)',
        '✅ Ограничения по использованию',
        '✅ Срок действия промокодов',
        '✅ Статистика использования',
        '✅ Интеграция с заказами'
      ],
      icon: <TrendingUp className="h-5 w-5" />
    },
    {
      name: 'Сообщения и коммуникации',
      status: 'working',
      progress: 85,
      functionality: [
        '✅ Система сообщений с клиентами',
        '✅ Интеграция с различными каналами',
        '✅ История переписки',
        '✅ Статусы сообщений',
        '✅ Назначение ответственных',
        '✅ Поддержка Telegram/WhatsApp'
      ],
      icon: <MessageSquare className="h-5 w-5" />
    },
    {
      name: 'Система задач',
      status: 'working',
      progress: 88,
      functionality: [
        '✅ Создание и управление задачами',
        '✅ Календарь задач',
        '✅ Напоминания',
        '✅ Периодические задачи',
        '✅ Категоризация',
        '✅ Статусы выполнения',
        '✅ Привязка к клиентам и заказам'
      ],
      icon: <CheckCircle className="h-5 w-5" />
    },
    {
      name: 'Ремонты и обслуживание',
      status: 'working',
      progress: 82,
      functionality: [
        '✅ Создание заявок на ремонт',
        '✅ Учет стоимости запчастей и работ',
        '✅ Статусы ремонта',
        '✅ История обслуживания',
        '✅ Привязка к клиентам',
        '✅ Интеграция с инвентарем'
      ],
      icon: <Wrench className="h-5 w-5" />
    },
    {
      name: 'Договоры и документы',
      status: 'working',
      progress: 80,
      functionality: [
        '✅ Шаблоны договоров',
        '✅ Автогенерация документов',
        '✅ Управление версиями',
        '✅ Привязка к заказам',
        '⚠️ Расширение шаблонов документов'
      ],
      icon: <FileText className="h-5 w-5" />
    },
    {
      name: 'Тарифы и услуги',
      status: 'working',
      progress: 85,
      functionality: [
        '✅ Создание тарифных планов',
        '✅ Настройка базовых цен',
        '✅ Управление услугами',
        '✅ Привязка к заказам',
        '✅ Гибкое ценообразование'
      ],
      icon: <TrendingUp className="h-5 w-5" />
    },
    {
      name: 'Настройки системы',
      status: 'working',
      progress: 90,
      functionality: [
        '✅ Пользовательские настройки',
        '✅ Конфигурация системы',
        '✅ Управление правами доступа',
        '✅ Интеграции с внешними системами',
        '✅ Система ролей и разрешений'
      ],
      icon: <Settings className="h-5 w-5" />
    },
    {
      name: 'Безопасность и авторизация',
      status: 'working',
      progress: 93,
      functionality: [
        '✅ Система аутентификации Supabase',
        '✅ Row Level Security (RLS)',
        '✅ JWT токены',
        '✅ Защищенные API endpoints',
        '✅ Разграничение доступа по ролям',
        '✅ Логирование действий пользователей'
      ],
      icon: <Shield className="h-5 w-5" />
    },
    {
      name: 'База данных и Backend',
      status: 'working',
      progress: 88,
      functionality: [
        '✅ PostgreSQL с Supabase',
        '✅ Edge Functions для API',
        '✅ RPC функции',
        '✅ Транзакционная поддержка',
        '✅ Логирование и мониторинг',
        '✅ Автоматические бэкапы',
        '⚠️ Оптимизация производительности запросов'
      ],
      icon: <Database className="h-5 w-5" />
    },
    {
      name: 'Frontend и UI/UX',
      status: 'working',
      progress: 90,
      functionality: [
        '✅ React 18 с TypeScript',
        '✅ shadcn/ui компоненты',
        '✅ Tailwind CSS стилизация',
        '✅ Адаптивный дизайн',
        '✅ Интерактивные формы',
        '✅ Состояние приложения (Context API)',
        '✅ Уведомления пользователю'
      ],
      icon: <FileText className="h-5 w-5" />
    },
    {
      name: 'Аналитика и отчеты',
      status: 'partial',
      progress: 75,
      functionality: [
        '✅ Статистика заказов',
        '✅ Анализ активности клиентов',
        '✅ Отчеты по инвентарю',
        '✅ Системные отчеты',
        '⚠️ Финансовые отчеты (базовый функционал)',
        '⚠️ Продвинутая аналитика (в разработке)',
        '⚠️ Экспорт данных'
      ],
      icon: <TrendingUp className="h-5 w-5" />
    },
    {
      name: 'Мобильная адаптация',
      status: 'working',
      progress: 85,
      functionality: [
        '✅ Responsive дизайн',
        '✅ Мобильная навигация',
        '✅ Touch-friendly интерфейс',
        '✅ Адаптивные таблицы',
        '✅ Мобильные формы',
        '⚠️ PWA функциональность'
      ],
      icon: <Smartphone className="h-5 w-5" />
    },
    {
      name: 'Фискальная интеграция',
      status: 'partial',
      progress: 60,
      functionality: [
        '✅ Настройка фискального оборудования',
        '✅ Конфигурация API ключей',
        '✅ Базовые структуры данных',
        '⚠️ Интеграция с ОФД (требует доработки)',
        '⚠️ Печать чеков (в разработке)',
        '❌ Полная интеграция 54-ФЗ'
      ],
      icon: <FileText className="h-5 w-5" />
    }
  ];


  return (
    <div className="space-y-6">
      <SummaryCard modules={modules} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {modules.map((module) => (
          <ModuleCard key={module.name} module={module} />
        ))}
      </div>

      <TechStackCard />
      <RecommendationsCard />
      <ConclusionCard />
    </div>
  );
};

export default SystemStatusReport;
